import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Modal, Form, Input, Select, Switch, Space, Tag, 
    Typography, message, Popconfirm, Tooltip, Badge, Divider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined, UnlockOutlined,
    KeyOutlined, UserOutlined, TeamOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchUsers, addUser, updateUser, deleteUser, changeUserPassword, lockUser, unlockUser, fetchRoles
} from '../actions';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const UserManagement = ({ users = [], roles = [], loading }) => {
    const dispatch = useDispatch();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();

    // Fetch users and roles when component mounts
    useEffect(() => {
        dispatch(fetchUsers());
        dispatch(fetchRoles());
    }, [dispatch]);

    // Ensure users and roles are always arrays
    const usersArray = Array.isArray(users) ? users : [];
    const rolesArray = Array.isArray(roles) ? roles : [];

    const handleAdd = () => {
        setEditingUser(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingUser(record);
        form.setFieldsValue({
            username: record.username,
            email: record.email,
            fullName: record.fullName,
            roleName: record.userRole?.name || record.role,
            active: record.active
        });
        setIsModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            if (editingUser) {
                // Update user
                const result = await dispatch(updateUser(editingUser.id, values));
                if (result.success) {
                    message.success('User information updated successfully');
                    setIsModalVisible(false);
                    dispatch(fetchUsers()); // Refresh users list
                } else {
                    message.error(result.error || 'Unable to update user information');
                }
            } else {
                // Create new user
                const result = await dispatch(addUser(values));
                if (result.success) {
                    message.success('New user created successfully');
                    setIsModalVisible(false);
                    dispatch(fetchUsers()); // Refresh users list
                } else {
                    message.error(result.error || 'Unable to create new user');
                }
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: 'Confirm user deletion',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete user "${record.username}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                dispatch(deleteUser(record.id)).then((result) => {
                    if (result.success) {
                        message.success('User deleted successfully');
                        dispatch(fetchUsers()); // Refresh users list
                    } else {
                        message.error(result.error || 'Unable to delete user');
                    }
                });
            }
        });
    };

    const handleChangePassword = (record) => {
        setEditingUser(record);
        passwordForm.resetFields();
        setIsPasswordModalVisible(true);
    };

    const handleSavePassword = async () => {
        try {
            const values = await passwordForm.validateFields();
            
            const result = await dispatch(changeUserPassword(editingUser.id, values));
            if (result.success) {
                message.success('Password changed successfully');
                setIsPasswordModalVisible(false);
            } else {
                message.error(result.error || 'Unable to change password');
            }
        } catch (error) {
            console.error('Password form validation failed:', error);
        }
    };

    const handleLockUser = (record) => {
        confirm({
            title: 'Confirm user lock',
            icon: <LockOutlined />,
            content: `Are you sure you want to lock user "${record.username}"?`,
            okText: 'Lock',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                dispatch(lockUser(record.id)).then((result) => {
                    if (result.success) {
                        message.success('User locked successfully');
                        dispatch(fetchUsers()); // Refresh users list
                    } else {
                        message.error(result.error || 'Unable to lock user');
                    }
                });
            }
        });
    };

    const handleUnlockUser = (record) => {
        dispatch(unlockUser(record.id)).then((result) => {
            if (result.success) {
                message.success('User unlocked successfully');
                dispatch(fetchUsers()); // Refresh users list
            } else {
                message.error(result.error || 'Unable to unlock user');
            }
        });
    };

    const getStatusBadge = (active) => {
        return active ? (
            <Badge status="success" text="Hoạt động" />
        ) : (
            <Badge status="error" text="Bị khóa" />
        );
    };

    const getRoleTag = (role, userRole) => {
        if (userRole) {
            return <Tag color="blue">{userRole.name}</Tag>;
        }
        
        const roleColors = {
            'Admin': 'red',
            'staff': 'orange', 
            'user': 'green'
        };
        
        const roleNames = {
            'Admin': 'Administrator',
            'staff': 'Staff',
            'user': 'User'
        };
        
        return <Tag color={roleColors[role] || 'default'}>{roleNames[role] || role}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            width: 150,
            render: (text, record) => (
                <Space>
                    <UserOutlined />
                    <Text strong>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 200,
        },
        {
            title: 'Role',
            key: 'role',
            width: 150,
            render: (_, record) => getRoleTag(record.role, record.userRole),
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: 120,
            render: (active) => getStatusBadge(active),
        },
        {
            title: 'Last Login',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            width: 180,
            render: (date) => date ? new Date(date).toLocaleString('en-US') : 'Never logged in',
        },
        {
            title: 'Actions',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Change Password">
                        <Button 
                            type="text" 
                            icon={<KeyOutlined />} 
                            onClick={() => handleChangePassword(record)}
                            size="small"
                        />
                    </Tooltip>
                    {record.active ? (
                        <Tooltip title="Lock User">
                            <Button 
                                type="text" 
                                danger
                                icon={<LockOutlined />} 
                                onClick={() => handleLockUser(record)}
                                size="small"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Unlock User">
                            <Button 
                                type="text" 
                                icon={<UnlockOutlined />} 
                                onClick={() => handleUnlockUser(record)}
                                size="small"
                                style={{ color: '#52c41a' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Delete">
                        <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />} 
                            onClick={() => handleDelete(record)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="user-management">
            <Card 
                title={
                    <Space>
                        <TeamOutlined />
                        <Title level={4} style={{ margin: 0 }}>User Management</Title>
                    </Space>
                }
                extra={
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAdd}
                    >
                        Add New
                    </Button>
                } 
                bordered={false}
            >
                <Table 
                    columns={columns} 
                    dataSource={usersArray.map(user => ({...user, key: user.id}))} 
                    loading={loading}
                    className="user-table"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* Add/Edit User Modal */}
            <Modal
                title={editingUser ? 'Edit User' : 'Add New User'}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
                okText={editingUser ? 'Update' : 'Create'}
                cancelText="Cancel"
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="userForm"
                >
                    <Form.Item
                        name="username"
                        label="Username"
                        rules={[
                            { required: true, message: 'Please enter username!' },
                            { min: 3, message: 'Username must be at least 3 characters!' }
                        ]}
                    >
                        <Input placeholder="Enter username" disabled={!!editingUser} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please enter email!' },
                            { type: 'email', message: 'Invalid email format!' }
                        ]}
                    >
                        <Input placeholder="Enter email" />
                    </Form.Item>

                    <Form.Item
                        name="fullName"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter full name!' }]}
                    >
                        <Input placeholder="Enter full name" />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                                { required: true, message: 'Please enter password!' },
                                { min: 6, message: 'Password must be at least 6 characters!' }
                            ]}
                        >
                            <Input.Password placeholder="Enter password" />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="roleName"
                        label="Role"
                        rules={[{ required: true, message: 'Please select a role!' }]}
                    >
                        <Select 
                            placeholder="Select role from system" 
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {rolesArray
                                .filter(role => role.isActive !== false)
                                .map(role => (
                                    <Option key={role.id} value={role.name}>
                                        {role.name} - {role.description}
                                    </Option>
                                ))
                            }
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="active"
                        label="Status"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Change Password Modal */}
            <Modal
                title={`Change password for user: ${editingUser?.username}`}
                open={isPasswordModalVisible}
                onOk={handleSavePassword}
                onCancel={() => setIsPasswordModalVisible(false)}
                okText="Change Password"
                cancelText="Cancel"
                confirmLoading={loading}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    name="passwordForm"
                >
                    <Form.Item
                        name="currentPassword"
                        label="Current Password"
                        rules={[{ required: true, message: 'Please enter current password!' }]}
                    >
                        <Input.Password placeholder="Enter current password" />
                    </Form.Item>

                    <Form.Item
                        name="newPassword"
                        label="New Password"
                        rules={[
                            { required: true, message: 'Please enter new password!' },
                            { min: 6, message: 'Password must be at least 6 characters!' }
                        ]}
                    >
                        <Input.Password placeholder="Enter new password" />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="Confirm New Password"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Please confirm new password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Password confirmation does not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirm new password" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagement;
